import styled from "styled-components/native";
import OptionHeader from "../OptionHeader";
import { FlatList } from "react-native";
import { ProductCategory } from "@utils/constants";
import { DisplayConfigurationV2 } from "@src/types/DiamondDetail";
import useScreenOptionsHooks from "../../../../hooks/useScreenOptionsHooks";
import { SpaceType } from "@components/atoms/Spacer/types";
import { CheckBox, Spacer } from "@components/atoms";
import DropDown from "../DropDown";
import RadioButton from "@components/atoms/RadioButton";
import { useEffect, useState } from "react";
import { useJewelryConfiguratorState } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import { css } from "styled-components/native";
import { DefaultTheme } from "styled-components/native";

type JewelryBuilderCheckBoxWithDropDownProps = {
  productType: ProductCategory;
  configObject: DisplayConfigurationV2;
  fromReviewPage?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  fromOrderCart?: boolean;
  fromRequest?: boolean;
  isRadioButton?: boolean;
};

const JewelryBuilderCheckBoxWithDropDown = ({
  productType,
  configObject,
  isRadioButton = false,
}: JewelryBuilderCheckBoxWithDropDownProps) => {
  const { theme } = useScreenOptionsHooks();
  const { addToOption, jcSelectedOption } = useJewelryConfiguratorState();

  const [selectedItem, setSelectedItem] = useState<string[]>([]);
  const [dropDownSelectedItem, setDropDownSelectedItem] = useState<string>("");
  const [selectedSubOption, setSelectedSubOption] = useState({});

  useEffect(() => {
    if (
      configObject?.selected_item?.selected_slugs ??
      jcSelectedOption.current.option_value[configObject.option_type]
        ?.selected_slugs
    ) {
      setSelectedItem(
        configObject?.selected_item?.selected_slugs ??
          jcSelectedOption.current.option_value[configObject.option_type]
            .selected_slugs
      );
    }
    if (
      configObject?.selected_item?.sub_options ||
      jcSelectedOption.current.option_value[configObject.option_type]
        ?.sub_options
    ) {
      const value = configObject?.option_value?.filter(
        (item) => item.has_sub_options
      )[0]?.value;
      setDropDownSelectedItem(
        configObject?.selected_item?.sub_options?.[value]?.[0] ??
          jcSelectedOption.current.option_value[configObject.option_type]
            ?.sub_options[value]?.[0]
      );
      setSelectedSubOption(
        configObject?.selected_item?.sub_options ||
          jcSelectedOption.current.option_value[configObject.option_type]
            ?.sub_options
      );
    }
  }, [
    configObject?.selected_item,
    jcSelectedOption.current.option_value[configObject.option_type],
  ]);

  return (
    <ContainerView>
      <HeaderContainer>
        <OptionHeader
          title={configObject.option_name}
          productType={productType}
          helperText={configObject.option_helper_text}
          disableToggleButton={configObject?.disable_modify_variation ?? false}
        />
      </HeaderContainer>
      <FlatList
        data={configObject.option_value.filter((item) => !item.has_sub_options)}
        renderItem={({ item, index }) => (
          <>
            <ItemContainer
              showBottomBorder={
                selectedItem?.length > 0 &&
                configObject.option_value.filter((i) => !i.has_sub_options)
                  .length -
                  1 ===
                  index
              }
            >
              {isRadioButton ? (
                <RadioButton
                  key={index}
                  checked={
                    selectedItem?.includes(item?.value) ||
                    selectedItem?.includes(item?.slug)
                  }
                  label={item.label}
                  onPress={() => {
                    if (
                      selectedItem?.includes(item?.value) ||
                      selectedItem?.includes(item?.slug)
                    ) {
                      setSelectedItem([]);
                      addToOption(configObject?.option_type, [
                        configObject,
                        {
                          selected_slugs: {},
                          sub_options: {},
                        },
                      ]);
                    } else {
                      addToOption(configObject?.option_type, [
                        configObject,
                        {
                          selected_slugs: [item?.value || item?.slug],
                          sub_options: {},
                        },
                      ]);
                      setSelectedItem([item?.value || item?.slug]);
                    }
                  }}
                  margin="0"
                  labelColor={
                    selectedItem?.includes(item?.value) ||
                    selectedItem?.includes(item?.slug)
                      ? theme["core-05"]
                      : theme["core-06"]
                  }
                />
              ) : (
                <CheckBox
                  text={item.label}
                  isChecked={
                    selectedItem?.includes(item?.value) ||
                    selectedItem?.includes(item?.slug)
                  }
                  onPress={() => {
                    if (
                      selectedItem?.includes(item?.value) ||
                      selectedItem?.includes(item?.slug)
                    ) {
                      setSelectedItem([]);
                      addToOption(configObject?.option_type, [
                        configObject,
                        {
                          selected_slugs: [item?.value || item?.slug],
                          sub_options: {},
                        },
                      ]);
                    } else {
                      addToOption(configObject?.option_type, [
                        configObject,
                        {
                          selected_slugs: [item?.value || item?.slug],
                          sub_options: selectedSubOption,
                        },
                      ]);
                      setSelectedItem([item?.value || item?.slug]);
                    }
                  }}
                  checkBoxSize={20}
                  labelColor={
                    selectedItem?.length > 0 &&
                    (selectedItem?.includes(item?.value) ||
                      selectedItem?.includes(item?.slug))
                      ? theme["core-05"]
                      : theme["core-06"]
                  }
                />
              )}
              <Spacer size={SpaceType.ExtraSmall} />
            </ItemContainer>
          </>
        )}
      />
      {selectedItem.length > 0 && (
        <Container>
          <DropDown
            defaultValue={dropDownSelectedItem}
            options={
              configObject.option_value.filter(
                (item) => item.has_sub_options
              )[0].sub_options
            }
            productType={productType}
            isSubOptions={
              configObject.option_value.filter((item) => item.has_sub_options)
                .length > 0
            }
            onSelect={(_value, selected) => {
              const value = configObject?.option_value.filter(
                (item) => item.has_sub_options
              )[0]?.value;
              setSelectedSubOption({ [value]: [_value] });
              setDropDownSelectedItem(_value);
              addToOption(configObject?.option_type, [
                configObject,
                {
                  selected_slugs: [],
                  sub_options: { [value]: [selected] },
                },
              ]);
            }}
          />
        </Container>
      )}
    </ContainerView>
  );
};

export default JewelryBuilderCheckBoxWithDropDown;

const ContainerView = styled.View`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-radius: ${SpaceType.Basic}px;
`;

const Container = styled.View`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-radius: 8px;
  padding: 12px 16px;
  width: 100%;
  gap: 12px;
`;

const HeaderContainer = styled.View`
  padding-top: ${SpaceType.Extra}px;
  padding-bottom: ${SpaceType.Extra}px;
  padding-left: ${SpaceType.Double}px;
  padding-right: ${SpaceType.Basic}px;
`;

const ItemContainer = styled.View<{
  showBottomBorder: boolean;
}>`
  padding-top: ${SpaceType.Basic}px;
  padding-bottom: ${SpaceType.Basic}px;
  padding-left: ${SpaceType.Double}px;
  border-top-width: 1px;
  border-top-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
  ${({ showBottomBorder }: { showBottomBorder: boolean }) =>
    showBottomBorder &&
    css`
      border-bottom-width: 1px;
      border-bottom-color: ${({ theme }) => theme["core-03"]};
    `}
`;
